import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  RefreshCcw,
  Download,
  Trash2,
  Database,
  Calendar,
  Filter,
  X,
  Phone,
  MessageCircle,
  StickyNote,
  Pencil,
  MoreVertical,
  Store,
  User,
  Check,
  ArrowLeftRight,
  Tag,
} from "lucide-react";
import { crmService } from "../crmApi";
import AddNoteDialog, { type NoteCategory } from "../components/AddNoteDialog";
import { LeadJourneySwapModal } from "@/components/LeadJourneySwapModal";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatList = (items: any[] | undefined, mapper: (item: any) => string) => {
  if (!items || items.length === 0) return "-";
  return items.map(mapper).join("\n\n");
};

const formatAddress = (lead: any) => {
  const pieces = [
    lead.primary_address_line1,
    lead.primary_address_line2,
    lead.primary_address_line3,
  ]
    .filter(Boolean)
    .join(", ");

  const extra = [
    lead.primary_address_landmark ? `Landmark: ${lead.primary_address_landmark}` : null,
    lead.primary_address_pincode ? `Pincode: ${lead.primary_address_pincode}` : null,
    lead.primary_address_lat && lead.primary_address_lng
      ? `Coords: ${lead.primary_address_lat}, ${lead.primary_address_lng}`
      : null,
  ]
    .filter(Boolean)
    .join(" | ");

  return extra ? `${pieces}\n${extra}` : pieces;
};

type LeadsResponse =
  | any[]
  | {
      count?: number;
      results?: any[];
      next?: string | null;
      previous?: string | null;
    };

const CRM_CSV_COLUMNS = [
  { id: "id", label: "ID" },
  { id: "trade_name", label: "Trade Name" },
  { id: "status", label: "Status" },
  { id: "follow_up_date", label: "Follow-up Date" },
  { id: "contact_name", label: "Contact Name" },
  { id: "contact_phone", label: "Phone" },
  { id: "bap_name", label: "BAP Name" },
  { id: "registered_name", label: "Registered Name" },
  { id: "role", label: "Role" },
  { id: "contact_type", label: "Contact Type" },
  { id: "contact_phone_type", label: "Phone Type" },
  { id: "contact_email", label: "Email" },
  { id: "lead_source_name", label: "Lead Source" },
  { id: "contact_other_lead_source", label: "Other Lead Source" },
  { id: "primary_address", label: "Primary Address" },
  { id: "address_category", label: "Address Category" },
  { id: "address_type", label: "Address Type" },
  { id: "assigned_to", label: "Assigned To" },
  { id: "lead_type", label: "Lead Type" },
  { id: "program_name", label: "Program" },
  { id: "website", label: "Website" },
  { id: "associated_sales_id", label: "Associated Sales ID" },
  { id: "additional_contacts", label: "Additional Contacts" },
  { id: "additional_addresses", label: "Additional Addresses" },
  { id: "notes", label: "Notes" },
  { id: "created_at", label: "Created At" },
  { id: "updated_at", label: "Updated At" },
  { id: "created_by_name", label: "Created By" },
  { id: "updated_by_name", label: "Updated By" },
];

// ─── Pagination Controls ──────────────────────────────────────────────────────

interface PaginationControlsProps {
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  totalCount: number;
  visibleCount: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (value: string) => void;
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  itemsPerPage,
  totalPages,
  totalCount,
  visibleCount,
  pageSizeOptions,
  onPageChange,
  onItemsPerPageChange,
}) => (
  <div className="mb-4 flex flex-row flex-wrap items-center justify-between gap-3 px-1">
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600 whitespace-nowrap">Show per page</span>
      <Select value={String(itemsPerPage)} onValueChange={onItemsPerPageChange}>
        <SelectTrigger className="w-[80px] h-8 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {pageSizeOptions.map((option) => (
            <SelectItem key={option} value={String(option)}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div className="text-sm text-gray-600 font-medium">
      Showing {visibleCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
      {visibleCount > 0
        ? `-${(currentPage - 1) * itemsPerPage + visibleCount}`
        : ""}{" "}
      of {totalCount}
    </div>
  </div>
);

const FooterPagination: React.FC<
  Pick<PaginationControlsProps, "currentPage" | "totalPages" | "onPageChange">
> = ({ currentPage, totalPages, onPageChange }) =>
  totalPages > 1 ? (
    <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm text-gray-600">
        Page {currentPage} of {totalPages}
      </div>

      <Pagination className="justify-between w-full sm:w-auto">
        <PaginationContent className="gap-2">
          <PaginationItem>
            <PaginationPrevious
              onClick={() => onPageChange(currentPage - 1)}
              className={`h-8 w-auto px-3 cursor-pointer ${
                currentPage === 1
                  ? "pointer-events-none opacity-50"
                  : "hover:bg-intellend-green/5"
              }`}
            />
          </PaginationItem>
          {Array.from({ length: totalPages }, (_, index) => index + 1).map(
            (page) => {
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <PaginationItem key={page}>
                    <PaginationLink
                      onClick={() => onPageChange(page)}
                      isActive={currentPage === page}
                      className={`h-8 w-8 text-xs ${
                        currentPage === page
                          ? "bg-intellend-green text-white hover:bg-intellend-green hover:text-white border-intellend-green"
                          : "hover:bg-intellend-green/5"
                      }`}
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                );
              }

              if (page === 2 || page === totalPages - 1) {
                return (
                  <PaginationItem key={page}>
                    <PaginationEllipsis className="h-8 w-8" />
                  </PaginationItem>
                );
              }

              return null;
            }
          )}
          <PaginationItem>
            <PaginationNext
              onClick={() => onPageChange(currentPage + 1)}
              className={`h-8 w-auto px-3 cursor-pointer ${
                currentPage === totalPages
                  ? "pointer-events-none opacity-50"
                  : "hover:bg-intellend-green/5"
              }`}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  ) : null;

// ─── Main Component ───────────────────────────────────────────────────────────

interface CRMLeadTableProps {
  displayMode?: "excel" | "modern";
  initialDateFilterType?: string;
  initialDateValue?: string;
  hideDateFilters?: boolean;
  title?: string;
}

const CRMLeadTable = ({
  displayMode = "excel",
  initialDateFilterType = "",
  initialDateValue = "",
  hideDateFilters = false,
  title,
}: CRMLeadTableProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_superuser;
  const allowUnassignedLeads = !!user?.allow_unassigned_leads;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(25);
  const [dateFilterType, setDateFilterType] = React.useState<string>(initialDateFilterType);
  const [dateValue, setDateValue] = React.useState<string>(initialDateValue);

  // ── Active filter state (what's actually sent to the API) ──────────────────
  const [tradeNameFilter, setTradeNameFilter] = React.useState("");
  const [contactNameSearch, setContactNameSearch] = React.useState("");
  const [contactPhoneSearch, setContactPhoneSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<string>("");

  // ── Temp filter state (edited inside the dialog before Apply) ──────────────
  const [tempTradeName, setTempTradeName] = React.useState("");
  const [tempContactName, setTempContactName] = React.useState("");
  const [tempContactPhone, setTempContactPhone] = React.useState("");
  const [tempDateFilterType, setTempDateFilterType] = React.useState<string>(initialDateFilterType);
  const [tempDateValue, setTempDateValue] = React.useState<string>(initialDateValue);
  const [tempStatus, setTempStatus] = React.useState<string>("");

  const [filterDialogOpen, setFilterDialogOpen] = React.useState(false);

  const [selectedLeadIds, setSelectedLeadIds] = React.useState<Set<number>>(new Set());
  const [showCsvColumnSelection, setShowCsvColumnSelection] = React.useState(false);
  const [showMobileMenu, setShowMobileMenu] = React.useState(false);
  const [selectedCsvColumns, setSelectedCsvColumns] = React.useState<Set<string>>(
    new Set([
      "id",
      "bap_name",
      "trade_name",
      "registered_name",
      "status",
      "role",
      "contact_name",
      "contact_type",
      "contact_phone_type",
      "contact_phone",
      "contact_email",
      "lead_source_name",
      "contact_other_lead_source",
      "primary_address",
      "address_category",
      "address_type",
      "assigned_to",
      "lead_type",
      "program_name",
      "website",
      "associated_sales_id",
      "additional_contacts",
      "additional_addresses",
      "notes",
      "created_at",
      "updated_at",
      "created_by_name",
      "updated_by_name",
    ])
  );

  // Add note dialog state
  const [addNote, setAddNote] = React.useState<{
    open: boolean;
    lead: any | null;
    category: NoteCategory;
  }>({ open: false, lead: null, category: "call" });

  // Journey swap modal state
  const [swapLead, setSwapLead] = React.useState<any | null>(null);

  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
  const pageSizeOptions = [5, 10, 25, 50, 100, 500];

  // Author name derived from auth user (User type only has username)
  const authorName = user?.username ?? "Agent";

  // ── Fetch available statuses for the filter dropdown ──────────────────────
  const { data: statusesData } = useQuery<{ id: number; name: string }[], Error>({
    queryKey: ["crm-statuses"],
    queryFn: async () => {
      const response = await crmService.getStatuses();
      return response.data as { id: number; name: string }[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  const availableStatuses = statusesData ?? [];

  const { data, isLoading, isError, refetch } = useQuery<LeadsResponse, Error>({
    queryKey: [
      "crm-leads",
      dateFilterType,
      dateValue,
      tradeNameFilter,
      contactNameSearch,
      contactPhoneSearch,
      statusFilter,
      allowUnassignedLeads,
    ] as const,
    queryFn: async ({ queryKey }) => {
      const [, _dateFilterType, _dateValue, _tradeNameFilter, _contactNameSearch, _contactPhoneSearch, _statusFilter] = queryKey;
      const response = await crmService.filterLeads({
        ...((_dateFilterType && _dateValue) ? { date_op: _dateFilterType, date_val: _dateValue } : {}),
        ...(_tradeNameFilter ? { trade_name: _tradeNameFilter } : {}),
        ...(_contactNameSearch ? { contact_name: _contactNameSearch } : {}),
        ...(_contactPhoneSearch ? { contact_phone: _contactPhoneSearch } : {}),
        ...(_statusFilter ? { status: _statusFilter } : {}),
      });
      return response.data as LeadsResponse;
    },
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

  React.useEffect(() => {
    if (isError) {
      toast({
        title: "Unable to load leads",
        description: "There was a problem fetching the CRM lead list.",
        variant: "destructive",
      });
    }
  }, [isError, toast]);

  React.useEffect(() => {
    tableContainerRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
  }, [currentPage, itemsPerPage]);

  const allLeads: any[] = Array.isArray(data) ? data : (data as any)?.results ?? [];
  const isServerPaginated =
    !Array.isArray(data) &&
    Array.isArray((data as any)?.results) &&
    typeof (data as any)?.count === "number";
  const totalCount = isServerPaginated
    ? ((data as any)?.count ?? allLeads.length)
    : allLeads.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  const visibleLeads = React.useMemo(() => {
    if (isServerPaginated) return allLeads;
    const start = (currentPage - 1) * itemsPerPage;
    return allLeads.slice(start, start + itemsPerPage);
  }, [allLeads, currentPage, itemsPerPage, isServerPaginated]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    setSelectedLeadIds(new Set());
  };

  const handleToggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedLeadIds(new Set(visibleLeads.map((lead) => lead.id)));
    } else {
      setSelectedLeadIds(new Set());
    }
  };

  const handleToggleSelectLead = (id: number, checked: boolean) => {
    const next = new Set(selectedLeadIds);
    if (checked) next.add(id);
    else next.delete(id);
    setSelectedLeadIds(next);
  };

  const handleBulkDelete = async () => {
    if (selectedLeadIds.size === 0) return;
    if (
      !window.confirm(
        `⚠️ Are you sure you want to delete ${selectedLeadIds.size} selected leads? This action cannot be undone.`
      )
    )
      return;

    try {
      await crmService.bulkDeleteLeads(Array.from(selectedLeadIds));
      toast({
        title: "Success",
        description: `Successfully deleted ${selectedLeadIds.size} leads.`,
      });
      setSelectedLeadIds(new Set());
      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to delete selected leads",
        variant: "destructive",
      });
    }
  };

  const escapeCsvValue = (value: any) => {
    const stringValue = value == null ? "" : String(value);
    return `"${stringValue.replace(/"/g, '""')}"`;
  };

  const formatCsvCell = (lead: any, column: string) => {
    switch (column) {
      case "primary_address":
        return escapeCsvValue(formatAddress(lead));
      case "additional_contacts":
        return escapeCsvValue(
          formatList(
            lead.additional_contacts,
            (c) =>
              `Name: ${c.name} | Role: ${c.role} | Phone: ${c.phone} | Type: ${c.type} | Email: ${c.email || "-"}`
          )
        );
      case "additional_addresses":
        return escapeCsvValue(
          formatList(
            lead.additional_addresses,
            (a) =>
              `${a.type}: ${a.address_line1}${a.address_line2 ? `, ${a.address_line2}` : ""}, ${a.address_line3} | ${a.address_pincode} | Cat: ${a.category || "-"}${a.address_landmark ? ` | LM: ${a.address_landmark}` : ""}`
          )
        );
      case "notes":
        return escapeCsvValue(
          formatList(
            lead.notes,
            (n) => `${n.category} / ${n.status} / ${n.sentiment}: ${n.text}`
          )
        );
      case "follow_up_date":
        return escapeCsvValue(
          lead.follow_up_date ? new Date(lead.follow_up_date).toLocaleDateString() : "-"
        );
      case "created_at":
      case "updated_at":
        return escapeCsvValue(
          lead[column] ? new Date(lead[column]).toLocaleString() : "-"
        );
      default:
        return escapeCsvValue(lead[column] ?? "-");
    }
  };

  const handleDownloadCsv = () => {
    const columns = Array.from(selectedCsvColumns);
    if (columns.length === 0) {
      toast({
        title: "No columns selected",
        description: "Select at least one column to export.",
        variant: "destructive",
      });
      return;
    }

    const headers = columns.map((col) => {
      const match = CRM_CSV_COLUMNS.find((item) => item.id === col);
      return escapeCsvValue(match?.label ?? col);
    });

    const rows = allLeads.map((lead) =>
      columns.map((col) => formatCsvCell(lead, col)).join(",")
    );

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `crm_data_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowCsvColumnSelection(false);
  };

  const handleBackupCrm = async () => {
    try {
      const response = await crmService.exportBackup();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      link.setAttribute("download", `crm_backup_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({
        title: "Backup Successful",
        description: "Your complete CRM data backup has been downloaded.",
      });
    } catch (error) {
      console.error("Backup failed:", error);
      toast({
        title: "Backup Failed",
        description: "There was a problem generating the CRM backup.",
        variant: "destructive",
      });
    }
  };

  const handleEditLead = (id: string | number) => {
    navigate(`/admin-dashboard/crm/leads/${id}`);
  };

  // ── Quick interaction handlers ──────────────────────────────────────────────

  const openAddNote = (lead: any, category: NoteCategory) => {
    setAddNote({ open: true, lead, category });
  };

  const handleCallLead = (lead: any) => {
    if (lead.contact_phone) {
      window.open(`tel:${lead.contact_phone}`, "_self");
    }
    openAddNote(lead, "call");
  };

  const handleWhatsAppLead = (lead: any) => {
    if (lead.contact_phone) {
      const digits = lead.contact_phone.replace(/\D/g, "");
      // Prepend India country code if not already present
      const number =
        digits.startsWith("91") && digits.length === 12 ? digits : `91${digits}`;
      window.open(`https://wa.me/${number}`, "_blank", "noopener,noreferrer");
    }
    openAddNote(lead, "whatsapp");
  };

  const handleNoteLead = (lead: any) => {
    openAddNote(lead, "internal");
  };

  const handleAddNoteSaved = () => {
    // Invalidate so the table re-fetches and follow_up_date reflects the new note
    queryClient.invalidateQueries({ queryKey: ["crm-leads"] });
  };

  // ── Guard: keep currentPage in bounds ──────────────────────────────────────
  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const description =
    displayMode === "modern"
      ? "Modern lead table layout will be applied here when available. Currently the table remains in the existing Excel-style format."
      : "Excel-style list of all leads with every CRM field captured from the lead form.";

  const hasActiveFilters =
    !!dateFilterType || !!dateValue || !!tradeNameFilter || !!contactNameSearch || !!contactPhoneSearch || !!statusFilter;

  // Count active filters for the badge
  const activeFilterCount = [
    tradeNameFilter,
    contactNameSearch,
    contactPhoneSearch,
    dateFilterType && dateValue ? dateValue : "",
    statusFilter,
  ].filter(Boolean).length;

  const handleApplyFilters = () => {
    setTradeNameFilter(tempTradeName);
    setContactNameSearch(tempContactName);
    setContactPhoneSearch(tempContactPhone);
    setStatusFilter(tempStatus);
    if (!hideDateFilters) {
      setDateFilterType(tempDateFilterType);
      setDateValue(tempDateValue);
    }
    setCurrentPage(1);
    setFilterDialogOpen(false);
  };

  const handleResetFilters = () => {
    setTempTradeName("");
    setTempContactName("");
    setTempContactPhone("");
    setTempStatus("");
    if (!hideDateFilters) {
      setTempDateFilterType("");
      setTempDateValue("");
    }
  };

  const handleClearFilters = () => {
    setTradeNameFilter("");
    setContactNameSearch("");
    setContactPhoneSearch("");
    setStatusFilter("");
    if (!hideDateFilters) {
      setDateFilterType("");
      setDateValue("");
    }
    setTempTradeName("");
    setTempContactName("");
    setTempContactPhone("");
    setTempStatus("");
    if (!hideDateFilters) {
      setTempDateFilterType("");
      setTempDateValue("");
    }
    setCurrentPage(1);
  };

  // Sync temp state when dialog opens
  const handleFilterDialogOpen = (open: boolean) => {
    if (open) {
      setTempTradeName(tradeNameFilter);
      setTempContactName(contactNameSearch);
      setTempContactPhone(contactPhoneSearch);
      setTempStatus(statusFilter);
      setTempDateFilterType(dateFilterType);
      setTempDateValue(dateValue);
    }
    setFilterDialogOpen(open);
  };

  return (
    <div className="w-full" data-display-mode={displayMode}>
      <div className="mx-auto w-full max-w-full rounded-xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm">
        {/* ── Header ── */}
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {title || "CRM: Lead Table"}
            </h1>
            <p className="mt-1 text-sm text-gray-500 hidden sm:block">{description}</p>
          </div>

          <div className="flex items-center gap-2">
            {/* Desktop + Mobile shared toolbar */}
            <div className="flex items-center gap-2">
              {/* ── Filter Dialog Button ── */}
              <Dialog open={filterDialogOpen} onOpenChange={handleFilterDialogOpen}>
                <DialogTrigger asChild>
                  <button
                    className={`flex items-center justify-center gap-2 h-9 px-4 bg-white hover:bg-gray-50 rounded-lg transition-all border border-gray-200 shadow-sm text-gray-700 hover:text-intellend-green font-semibold text-sm ${hasActiveFilters ? "border-intellend-green/60 text-intellend-green bg-intellend-green/5" : ""}`}
                    title="Filter"
                  >
                    <Filter className={`h-4 w-4 ${hasActiveFilters ? "text-intellend-green" : ""}`} />
                    <span className="hidden sm:inline">Filter</span>
                    {hasActiveFilters && (
                      <span className="ml-1 px-1.5 py-0.5 bg-intellend-green text-white text-[10px] rounded-full shadow-sm">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                </DialogTrigger>

                <DialogContent className="w-[95vw] max-w-lg flex flex-col p-0 overflow-hidden border-none shadow-2xl">
                  <DialogHeader className="p-5 border-b flex flex-row items-center justify-between space-y-0 bg-gray-50/50">
                    <div className="flex-1">
                      <DialogTitle className="text-lg font-bold flex items-center gap-2">
                        <Filter className="h-5 w-5 text-intellend-green" />
                        Filters
                      </DialogTitle>
                      <p className="text-xs text-gray-400 italic mt-0.5">
                        Changes apply only when you click "Apply"
                      </p>
                    </div>
                    <div className="flex items-center gap-2 md:pr-8 md:border-l md:pl-4">
                      <Button
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300 font-bold shadow-sm h-9 px-4 text-sm"
                        onClick={handleResetFilters}
                      >
                        Reset
                      </Button>
                      <Button
                        className="bg-intellend-green hover:bg-intellend-green/90 text-white font-semibold h-9 px-4 text-sm"
                        onClick={handleApplyFilters}
                      >
                        <Check className="h-4 w-4 mr-1.5" />
                        Apply
                      </Button>
                    </div>
                  </DialogHeader>

                  <div className="overflow-y-auto p-5 bg-white space-y-5">
                     {/* Status */}
                     <div className="space-y-1.5">
                       <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                         <Tag className="h-3.5 w-3.5 text-intellend-green" /> Status
                       </label>
                       <Select
                         value={tempStatus}
                         onValueChange={(val) => setTempStatus(val === "__all__" ? "" : val)}
                       >
                         <SelectTrigger className="h-10 text-sm w-full">
                           <SelectValue placeholder="All statuses" />
                         </SelectTrigger>
                         <SelectContent>
                           <SelectItem value="__all__">All statuses</SelectItem>
                           {availableStatuses.map((s) => (
                             <SelectItem key={s.id} value={s.name}>
                               <span className="flex items-center gap-2">
                                 {s.name}
                               </span>
                             </SelectItem>
                           ))}
                         </SelectContent>
                       </Select>
                       {tempStatus && (
                         <div className="flex flex-wrap gap-1 mt-1">
                           <Badge
                             variant="secondary"
                             className="text-xs flex items-center gap-1 bg-intellend-green/10 text-intellend-green border border-intellend-green/20"
                           >
                             {tempStatus}
                             <button
                               type="button"
                               onClick={() => setTempStatus("")}
                               className="ml-0.5 hover:text-red-500"
                             >
                               <X className="h-3 w-3" />
                             </button>
                           </Badge>
                         </div>
                       )}
                     </div>

                     {/* Trade Name */}
                     <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                        <Store className="h-3.5 w-3.5 text-intellend-green" /> Trade Name
                      </label>
                      <Input
                        type="text"
                        placeholder="Filter by trade name…"
                        value={tempTradeName}
                        onChange={(e) => setTempTradeName(e.target.value)}
                        className="h-10 text-sm"
                      />
                    </div>

                    {/* Contact Name */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5 text-intellend-green" /> Contact Name
                        <span className="text-[10px] font-normal text-gray-400 normal-case tracking-normal">(primary + all contacts)</span>
                      </label>
                      <Input
                        type="text"
                        placeholder="Search by contact name…"
                        value={tempContactName}
                        onChange={(e) => setTempContactName(e.target.value)}
                        className="h-10 text-sm"
                      />
                    </div>

                    {/* Contact Phone */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5 text-intellend-green" /> Contact Number
                        <span className="text-[10px] font-normal text-gray-400 normal-case tracking-normal">(primary + all contacts)</span>
                      </label>
                      <Input
                        type="text"
                        placeholder="Search by contact number…"
                        value={tempContactPhone}
                        onChange={(e) => setTempContactPhone(e.target.value)}
                        className="h-10 text-sm"
                      />
                    </div>

                    {/* Follow-up Date */}
                    <div className={`space-y-1.5 rounded-lg p-3 border ${hideDateFilters ? "bg-gray-50 opacity-60" : "bg-white border-gray-200"}`}>
                      <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 text-intellend-green" /> Follow-up Date
                        {hideDateFilters && (
                          <span className="ml-auto text-[10px] font-normal text-gray-400 normal-case tracking-normal">locked for this view</span>
                        )}
                      </label>
                      <div className="flex gap-2">
                        <Select
                          value={tempDateFilterType}
                          onValueChange={(val) => !hideDateFilters && setTempDateFilterType(val)}
                          disabled={hideDateFilters}
                        >
                          <SelectTrigger className={`flex-1 h-10 text-sm ${hideDateFilters ? "cursor-not-allowed opacity-70" : ""}`}>
                            <SelectValue placeholder="Operator" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="on">On</SelectItem>
                            <SelectItem value="before">Before</SelectItem>
                            <SelectItem value="on_or_before">On or Before</SelectItem>
                            <SelectItem value="after">After</SelectItem>
                            <SelectItem value="on_or_after">On or After</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="date"
                          value={tempDateValue}
                          onChange={(e) => !hideDateFilters && setTempDateValue(e.target.value)}
                          disabled={hideDateFilters}
                          className={`flex-1 h-10 text-sm ${hideDateFilters ? "cursor-not-allowed opacity-70" : ""}`}
                        />
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="h-9 px-2 text-gray-500 hover:text-red-500"
                  title="Clear all filters"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}

              {isAdmin && selectedLeadIds.size > 0 && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  className="hidden md:flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" /> Delete ({selectedLeadIds.size})
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleBackupCrm}
                className="hidden md:flex items-center gap-2 bg-intellend-green/5 border-intellend-green/20 hover:bg-intellend-green/10 text-intellend-green"
                title="Download complete CRM backup for bulk upload"
              >
                <Database className="h-4 w-4" /> Backup CRM
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowCsvColumnSelection(true)}
                className="hidden md:flex items-center gap-2"
              >
                <Download className="h-4 w-4" /> CRM Data CSV
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="hidden md:flex items-center gap-2"
              >
                <RefreshCcw className="h-4 w-4" /> Refresh
              </Button>
            </div>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden flex items-center justify-center h-9 w-9 bg-white hover:bg-gray-50 rounded-lg transition-all border border-gray-200 shadow-sm text-gray-700 hover:text-intellend-green font-semibold ml-auto"
              title="Menu"
            >
              <svg
                className={`h-5 w-5 transition-transform ${showMobileMenu ? "rotate-45" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {showMobileMenu && (
          <div className="md:hidden flex flex-col gap-3 w-full mb-6 pt-3 border-t border-gray-200">
            {isAdmin && selectedLeadIds.size > 0 && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => {
                  handleBulkDelete();
                  setShowMobileMenu(false);
                }}
                className="flex items-center justify-start gap-2 h-10 px-4 w-full"
              >
                <Trash2 className="h-4 w-4" /> Delete ({selectedLeadIds.size})
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                handleBackupCrm();
                setShowMobileMenu(false);
              }}
              className="flex items-center justify-start gap-2 h-10 px-4 bg-intellend-green/5 border-intellend-green/20 text-intellend-green w-full"
            >
              <Database className="h-4 w-4" /> Backup CRM
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setShowCsvColumnSelection(true);
                setShowMobileMenu(false);
              }}
              className="flex items-center justify-start gap-2 h-10 px-4 w-full"
            >
              <Download className="h-4 w-4" /> CRM Data CSV
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                refetch();
                setShowMobileMenu(false);
              }}
              className="flex items-center justify-start gap-2 h-10 px-4 w-full"
            >
              <RefreshCcw className="h-4 w-4" /> Refresh
            </Button>
          </div>
        )}

        {/* ── Table ── */}
        {isLoading ? (
          <div className="py-20 text-center text-sm text-gray-500">Loading leads...</div>
        ) : isError ? (
          <div className="py-20 text-center text-sm text-red-600">
            Unable to load lead list.
          </div>
        ) : (
          <>
            <PaginationControls
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalPages={totalPages}
              totalCount={totalCount}
              visibleCount={visibleLeads.length}
              pageSizeOptions={pageSizeOptions}
              onPageChange={handlePageChange}
              onItemsPerPageChange={(value) => {
                setItemsPerPage(Number(value));
                setCurrentPage(1);
              }}
            />

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              <div
                ref={tableContainerRef}
                className="relative max-h-[68vh] overflow-x-auto overflow-y-auto"
              >
                <div className="min-w-max">
                  <table className="w-full text-xs min-w-max">
                    <TableHeader className="bg-gray-50">
                      <TableRow className="sticky top-0 z-20 border-b bg-gray-50">
                        {isAdmin && (
                          <TableHead className="w-[40px]">
                            <Checkbox
                              checked={
                                visibleLeads.length > 0 &&
                                selectedLeadIds.size === visibleLeads.length
                              }
                              onCheckedChange={(checked) =>
                                handleToggleSelectAll(!!checked)
                              }
                            />
                          </TableHead>
                        )}
                        <TableHead>ID</TableHead>
                        <TableHead>Trade Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Follow-up Date</TableHead>
                        <TableHead>Contact Name</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>BAP Name</TableHead>
                        <TableHead>Registered Name</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Contact Type</TableHead>
                        <TableHead>Phone Type</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Lead Source</TableHead>
                        <TableHead>Other Lead Source</TableHead>
                        <TableHead className="min-w-[240px]">Primary Address</TableHead>
                        <TableHead>Address Category</TableHead>
                        <TableHead>Address Type</TableHead>
                        <TableHead>Assigned To</TableHead>
                        <TableHead>Lead Type</TableHead>
                        <TableHead>Program</TableHead>
                        <TableHead>Website</TableHead>
                        <TableHead>Associated Sales ID</TableHead>
                        <TableHead className="min-w-[260px]">Additional Contacts</TableHead>
                        <TableHead className="min-w-[260px]">Additional Addresses</TableHead>
                        <TableHead className="min-w-[220px]">Notes</TableHead>
                        <TableHead>Created At</TableHead>
                        <TableHead>Updated At</TableHead>
                        <TableHead>Created By</TableHead>
                        <TableHead>Updated By</TableHead>
                        <TableHead className="sticky right-0 top-0 z-50 bg-gray-50 border-l border-slate-200 min-w-[36px] sm:min-w-[130px]">
                          <span className="hidden sm:inline">Actions</span>
                          <span className="sm:hidden sr-only">Actions</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {visibleLeads.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={isAdmin ? 31 : 30}
                            className="text-center py-10 text-sm text-gray-500"
                          >
                            No leads found.
                          </TableCell>
                        </TableRow>
                      ) : (
                        visibleLeads.map((lead) => (
                          <TableRow key={lead.id} className="hover:bg-gray-50">
                            {isAdmin && (
                              <TableCell>
                                <Checkbox
                                  checked={selectedLeadIds.has(lead.id)}
                                  onCheckedChange={(checked) =>
                                    handleToggleSelectLead(lead.id, !!checked)
                                  }
                                />
                              </TableCell>
                            )}
                            <TableCell>{lead.id}</TableCell>
                            <TableCell>{lead.trade_name}</TableCell>
                            <TableCell>{lead.status_name || lead.status || "-"}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {lead.follow_up_date &&
                                  new Date(lead.follow_up_date).setHours(0, 0, 0, 0) <
                                    new Date().setHours(0, 0, 0, 0) && (
                                    <div
                                      className="h-2 w-2 rounded-full bg-red-500 animate-pulse"
                                      title="Follow-up overdue"
                                    />
                                  )}
                                {lead.follow_up_date
                                  ? new Date(lead.follow_up_date).toLocaleDateString()
                                  : "-"}
                                {lead.follow_up_time && (
                                  <span className="text-[10px] text-gray-400">
                                    ({lead.follow_up_time})
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{lead.contact_name}</TableCell>
                            <TableCell>{lead.contact_phone}</TableCell>
                            <TableCell>{lead.bap_name_display || lead.bap_name || "-"}</TableCell>
                            <TableCell>{lead.registered_name || "-"}</TableCell>
                            <TableCell>{lead.role}</TableCell>
                            <TableCell>{lead.contact_type}</TableCell>
                            <TableCell>{lead.contact_phone_type}</TableCell>
                            <TableCell>{lead.contact_email || "-"}</TableCell>
                            <TableCell>
                              {lead.lead_source_name || lead.contact_lead_source || "-"}
                            </TableCell>
                            <TableCell>{lead.contact_other_lead_source || "-"}</TableCell>
                            <TableCell className="whitespace-pre-line break-words min-w-[240px]">
                              {formatAddress(lead)}
                            </TableCell>
                            <TableCell>{lead.address_category || "-"}</TableCell>
                            <TableCell>{lead.address_type || "-"}</TableCell>
                            <TableCell>
                              {lead.assigned_to
                                ? `(${lead.assigned_to}) ${lead.assigned_to_name || ""}`
                                : "-"}
                            </TableCell>
                            <TableCell>{lead.lead_type || "-"}</TableCell>
                            <TableCell>{lead.program_name || "-"}</TableCell>
                            <TableCell>{lead.website || "-"}</TableCell>
                            <TableCell>{lead.associated_sales_id || "-"}</TableCell>
                            <TableCell className="whitespace-pre-line break-words min-w-[260px]">
                              {formatList(
                                lead.additional_contacts,
                                (c) =>
                                  `Name: ${c.name} | Role: ${c.role} | Phone: ${c.phone} | Type: ${c.type} | Email: ${c.email || "-"}`
                              )}
                            </TableCell>
                            <TableCell className="whitespace-pre-line break-words min-w-[260px]">
                              {formatList(
                                lead.additional_addresses,
                                (a) =>
                                  `${a.type}: ${a.address_line1}${a.address_line2 ? `, ${a.address_line2}` : ""}, ${a.address_line3} | ${a.address_pincode} | Cat: ${a.category || "-"}${a.address_landmark ? ` | LM: ${a.address_landmark}` : ""}`
                              )}
                            </TableCell>
                            <TableCell className="whitespace-pre-line break-words min-w-[220px]">
                              {formatList(
                                lead.notes,
                                (n) =>
                                  `${n.category} / ${n.status} / ${n.sentiment}: ${n.text}`
                              )}
                            </TableCell>
                            <TableCell>
                              {lead.created_at
                                ? new Date(lead.created_at).toLocaleString()
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {lead.updated_at
                                ? new Date(lead.updated_at).toLocaleString()
                                : "-"}
                            </TableCell>
                            <TableCell>{lead.created_by_name || "-"}</TableCell>
                            <TableCell>{lead.updated_by_name || "-"}</TableCell>

                            {/* ── Quick Actions + Edit (sticky right) ── */}
                            <TableCell className="sticky right-0 z-10 bg-slate-50 border-l border-slate-200">
                              {/* Desktop: inline icon buttons */}
                              <div className="hidden sm:flex items-center gap-0.5">
                                <button
                                  type="button"
                                  title="Log Call"
                                  onClick={() => handleCallLead(lead)}
                                  className="h-7 w-7 flex items-center justify-center rounded hover:bg-blue-50 text-blue-500 hover:text-blue-700 transition-colors"
                                >
                                  <Phone className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  title="Log WhatsApp"
                                  onClick={() => handleWhatsAppLead(lead)}
                                  className="h-7 w-7 flex items-center justify-center rounded hover:bg-green-50 text-green-500 hover:text-green-700 transition-colors"
                                >
                                  <MessageCircle className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  title="Add Note"
                                  onClick={() => handleNoteLead(lead)}
                                  className="h-7 w-7 flex items-center justify-center rounded hover:bg-amber-50 text-amber-500 hover:text-amber-700 transition-colors"
                                >
                                  <StickyNote className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  title="View Journeys"
                                  onClick={() => setSwapLead(lead)}
                                  className="h-7 w-7 flex items-center justify-center rounded hover:bg-purple-50 text-purple-500 hover:text-purple-700 transition-colors"
                                >
                                  <ArrowLeftRight className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  title="Edit Lead"
                                  onClick={() => handleEditLead(lead.id)}
                                  className="h-7 w-7 flex items-center justify-center rounded hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              </div>

                              {/* Mobile: collapsed dropdown */}
                              <div className="flex sm:hidden items-center justify-center">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      title="Actions"
                                      className="h-7 w-7 flex items-center justify-center rounded hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
                                    >
                                      <MoreVertical className="h-4 w-4" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-44">
                                    <DropdownMenuItem
                                      onClick={() => handleCallLead(lead)}
                                      className="flex items-center gap-2 text-blue-600 focus:text-blue-700"
                                    >
                                      <Phone className="h-4 w-4" /> Log Call
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleWhatsAppLead(lead)}
                                      className="flex items-center gap-2 text-green-600 focus:text-green-700"
                                    >
                                      <MessageCircle className="h-4 w-4" /> Log WhatsApp
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleNoteLead(lead)}
                                      className="flex items-center gap-2 text-amber-600 focus:text-amber-700"
                                    >
                                      <StickyNote className="h-4 w-4" /> Add Note
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => setSwapLead(lead)}
                                      className="flex items-center gap-2 text-purple-600 focus:text-purple-700"
                                    >
                                      <ArrowLeftRight className="h-4 w-4" /> View Journeys
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleEditLead(lead.id)}
                                      className="flex items-center gap-2 text-gray-600 focus:text-gray-800"
                                    >
                                      <Pencil className="h-4 w-4" /> Edit Lead
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </table>
                </div>
              </div>
            </div>

            <FooterPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      {/* ── CSV Column Selection Dialog ── */}
      <Dialog open={showCsvColumnSelection} onOpenChange={setShowCsvColumnSelection}>
        <DialogContent className="max-w-3xl mx-auto">
          <DialogHeader>
            <DialogTitle>CRM CSV Export</DialogTitle>
            <p className="text-sm text-gray-600">
              Select the columns to include in the exported CSV. The download will reflect
              your current data access and selected CRM fields.
            </p>
          </DialogHeader>
          <div className="grid gap-3 py-4 max-h-[60vh] overflow-y-auto">
            {CRM_CSV_COLUMNS.map((column) => (
              <label
                key={column.id}
                className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 hover:border-intellend-green/40"
              >
                <Checkbox
                  checked={selectedCsvColumns.has(column.id)}
                  onCheckedChange={(checked) => {
                    if (typeof checked !== "boolean") return;
                    const next = new Set(selectedCsvColumns);
                    if (checked) next.add(column.id);
                    else next.delete(column.id);
                    setSelectedCsvColumns(next);
                  }}
                />
                <span>{column.label}</span>
              </label>
            ))}
          </div>
          <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={() => setShowCsvColumnSelection(false)}>
              Cancel
            </Button>
            <Button onClick={handleDownloadCsv} className="flex items-center gap-2">
              <Download className="h-4 w-4" /> Download CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add Note Dialog ── */}
      <AddNoteDialog
        open={addNote.open}
        category={addNote.category}
        leadId={addNote.lead?.id ?? null}
        leadLabel={
          addNote.lead
            ? [
                addNote.lead.trade_name,
                addNote.lead.contact_name,
                addNote.lead.contact_phone,
              ]
                .filter(Boolean)
                .join(" · ")
            : undefined
        }
        authorName={authorName}
        onClose={() => setAddNote((prev) => ({ ...prev, open: false }))}
        onSaved={handleAddNoteSaved}
      />

      {/* ── Journey Swap Modal ── */}
      <LeadJourneySwapModal
        phone={swapLead?.contact_phone ?? null}
        tradeName={swapLead?.trade_name}
        currentLeadId={swapLead?.id ?? null}
        isOpen={!!swapLead}
        onClose={() => setSwapLead(null)}
      />
    </div>
  );
};

export default CRMLeadTable;
